import { useCallback, useEffect, useRef, useState } from "react"

import { UseFormSetValue } from "react-hook-form"

import { TurnstileWidgetProps, TurnstileWidgetRef } from "@/lib/components/turnstile"

interface UseTurnstileOptions {
  setValue: UseFormSetValue<any> // from react-hook-form
  fieldName?: string // defaults to "turnstileToken"
  onError?: () => void // optional custom error handler
  activateOnView?: boolean
  rootMargin?: string
  threshold?: number
}

interface UseTurnstileReturn {
  isReady: boolean // true when token is valid
  isActive: boolean // true when widget is allowed to render
  hasError: boolean // true if turnstile failed
  widgetRef: React.RefObject<TurnstileWidgetRef>
  targetRef: React.RefObject<HTMLFormElement>
  widgetProps: TurnstileWidgetProps // spread onto <TurnstileWidget>
  reset: () => void // call after form submission
}

/**
 * Hook to manage Cloudflare Turnstile widget state and callbacks
 * Reduces boilerplate for form integration
 */
export function useTurnstile({
  setValue,
  fieldName = "turnstileToken",
  onError,
  activateOnView = false,
  rootMargin = "200px 0px",
  threshold = 0,
}: UseTurnstileOptions): UseTurnstileReturn {
  const [isReady, setIsReady] = useState(false)
  const [isActive, setIsActive] = useState(!activateOnView)
  const [hasError, setHasError] = useState(false)
  const widgetRef = useRef<TurnstileWidgetRef>(null)
  const targetRef = useRef<HTMLFormElement>(null)

  // Use ref to keep onError callback stable and prevent infinite loops
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  const handleVerify = useCallback(
    (token: string) => {
      setValue(fieldName, token, { shouldValidate: true })
      setIsReady(true)
      setHasError(false) // clear error on successful verification
    },
    [setValue, fieldName],
  )

  const handleExpire = useCallback(() => {
    setValue(fieldName, "", { shouldValidate: false })
    setIsReady(false)
  }, [setValue, fieldName])

  const handleError = useCallback(() => {
    setHasError(true)
    onErrorRef.current?.()
  }, [])

  const reset = useCallback(() => {
    widgetRef.current?.reset()
  }, [])

  useEffect(() => {
    if (!activateOnView || isActive) return
    const target = targetRef.current
    if (!target) return

    if (!("IntersectionObserver" in window)) {
      setIsActive(true)
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setIsActive(true)
          observer.disconnect()
        }
      },
      { rootMargin, threshold },
    )

    observer.observe(target)

    return () => observer.disconnect()
  }, [activateOnView, isActive, rootMargin, threshold])

  const widgetProps: TurnstileWidgetProps = {
    onVerify: handleVerify,
    onExpire: handleExpire,
    onError: handleError,
  }

  return {
    isReady,
    isActive,
    hasError,
    widgetRef,
    targetRef,
    widgetProps,
    reset,
  }
}
